import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Checkbox,
  Divider as MuiDivider,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  List,
  ListItem,
  TextField,
  Typography as MuiTypography,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Authorization Revocation
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Authorization Revocation</Typography>
          </Breadcrumbs>
          <Divider my={6} />
          <Typography variant="body1" paragraph>
            This form serves as a means for individuals to revoke their previously given authorization for the use and
            disclosure of their Protected Health Information (PHI) as per the Health Insurance Portability and
            Accountability Act (HIPAA) requirements. Completing and submitting this form will cease any further use or
            disclosure of your PHI based on the authorization form you previously provided, except to the extent that
            action has already been taken relying on your authorization.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Authorization Revocation Form
          </Typography>

          <form>
            <List>
              <ListItem>
                <Typography>
                  <b>Personal Information:</b>
                </Typography>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
                  <TextField label="Full Name" variant="outlined" fullWidth margin="normal" />
                  <TextField label="Date of Birth" variant="outlined" fullWidth margin="normal" />
                  <TextField label="Patient ID (if applicable)" variant="outlined" fullWidth margin="normal" />
                </Box>
              </ListItem>

              <ListItem>
                <Box display="flex" flexDirection="column" sx={{ lineHeight: 2 }}>
                  <Typography component="span" sx={{ mb: 2 }}>
                    <b>Authorization Revocation</b>
                  </Typography>
                  <Typography component="span" sx={{ mb: 7 }}>
                    By this notice, I withdraw my consent for the use or sharing of my Protected Health Information
                    (PHI) as detailed below:
                  </Typography>
                  <Typography component="span" sx={{ mb: 1 }}>
                    Type of PHI to withdraw consent for:
                  </Typography>
                  <Box sx={{ mb: 7 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox />}
                        label="Withdraw All Protected Health Information (PHI)"
                      />
                      <FormControlLabel control={<Checkbox />} label="Medical history" />
                      <FormControlLabel control={<Checkbox />} label="Treatment records" />
                      <FormControlLabel control={<Checkbox />} label="Billing information" />
                      <FormControlLabel control={<Checkbox />} label="Insurance details" />
                      <FormControlLabel control={<Checkbox />} label="Other (please specify below)" />
                      <TextField fullWidth label="Specify other" margin="normal" variant="outlined" />
                    </FormGroup>
                  </Box>
                  <Typography component="span" sx={{ mb: 1 }}>
                    Entities or individuals previously authorized for use or disclosure to withdraw consent for:
                  </Typography>
                  <Box sx={{ mb: 7 }}>
                    <FormGroup>
                      <FormControlLabel control={<Checkbox />} label="Withdraw All Entities and Individuals" />
                      <FormControlLabel control={<Checkbox />} label="Healthcare providers" />
                      <FormControlLabel control={<Checkbox />} label="Insurance companies" />
                      <FormControlLabel control={<Checkbox />} label="Family members" />
                      <FormControlLabel control={<Checkbox />} label="Legal representatives" />
                    </FormGroup>
                  </Box>
                  <Typography component="span" sx={{ mb: 1 }}>
                    Intended purpose for the use or disclosure no longer allowed:
                  </Typography>
                  <FormGroup>
                    <FormControlLabel control={<Checkbox />} label="Withdraw All Purposes" />
                    <FormControlLabel control={<Checkbox />} label="Treatment activities" />
                    <FormControlLabel control={<Checkbox />} label="Payment processes" />
                    <FormControlLabel control={<Checkbox />} label="Healthcare operations" />
                    <FormControlLabel control={<Checkbox />} label="Research initiatives" />
                    <FormControlLabel control={<Checkbox />} label="Other" />
                    <TextField fullWidth label="Specify other" margin="normal" variant="outlined" />
                  </FormGroup>
                </Box>
              </ListItem>

              <ListItem>
                <Typography>
                  <b>Revocation Endorsement:</b>
                </Typography>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
                  <TextField label="Date of Authorization Revocation" variant="outlined" fullWidth margin="normal" />
                  <TextField label="Signature" variant="outlined" fullWidth margin="normal" multiline rows={4} />
                </Box>
              </ListItem>

              <ListItem>
                <Typography>
                  By submitting this form, you are revoking your consent for the use and disclosure of your Protected
                  Health Information (PHI) as specified in the authorization form you previously signed. This revocation
                  does not affect any use or disclosures made prior to the receipt of this form.
                </Typography>
              </ListItem>
            </List>
          </form>

          <Typography variant="body2" color="textSecondary" align="center" paragraph>
            Note: It may take up to 30 days for the revocation to be processed and become effective.
          </Typography>

          <Box display="flex" flexDirection="column" sx={{ mb: 2 }}>
            <Typography variant="h5" gutterBottom>
              Download
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" sx={{ lineHeight: 2 }}>
            <Typography component="span">
              To download the Authorization Revocation Form, please click the link below:
            </Typography>
          </Box>
          <List>
            <ListItem>
              <Typography component="span" sx={{ mr: 2 }}>
                [
              </Typography>
              <Link href="/static/docs/HIPAA_Authorization_Revocation_Form.pdf" target="_blank">
                HIPAA Authorization Revocation Form
              </Link>
              <Typography component="span" sx={{ ml: 2 }}>
                {" "}
                ]
              </Typography>
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
